import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Package, Plus, Trash2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * ProductTiersEditor — Mengen-Staffelpreise für Produkte.
 * Globale Tier-Config gilt für alle Produkte. Bei Bedarf können die Tiers
 * pro Produkt überschrieben werden (zukünftiges Feature; für jetzt nur global).
 */
const ProductTiersEditor = () => {
  const [tiers, setTiers] = useState([]);
  const [active, setActive] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/admin/shop/product-tiers`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setTiers(res.data.tiers || []);
        setActive(res.data.is_active !== false);
      } catch (e) {
        console.error(e);
        toast.error('Fehler beim Laden der Produkt-Staffelung');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const updateTier = (idx, key, value) => {
    setTiers((prev) => prev.map((t, i) => (i === idx ? { ...t, [key]: value } : t)));
  };

  const addTier = () => {
    const lastMin = tiers.length > 0 ? Number(tiers[tiers.length - 1].min_qty || 0) + 1 : 1;
    setTiers((prev) => [
      ...prev,
      { min_qty: lastMin, max_qty: null, discount_percent: 0, label: `${lastMin}+ Stk.` },
    ]);
  };

  const removeTier = (idx) => setTiers((prev) => prev.filter((_, i) => i !== idx));

  const save = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const payload = {
        tiers: tiers.map((t) => ({
          min_qty: Number(t.min_qty) || 1,
          max_qty: t.max_qty === null || t.max_qty === '' ? null : Number(t.max_qty),
          discount_percent: Number(t.discount_percent) || 0,
          label: t.label || null,
        })),
        is_active: !!active,
        product_id: null, // Global-Config
      };
      await axios.put(`${API}/admin/shop/product-tiers`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Produkt-Staffelung gespeichert');
    } catch (e) {
      console.error(e);
      toast.error('Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return null;

  return (
    <Card data-testid="product-tiers-editor">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Package className="w-5 h-5 text-green-500" />
          Produkte · Mengen-Staffelung
        </CardTitle>
        <CardDescription>
          Je mehr der Kunde kauft, desto günstiger wird der Stückpreis.
          Diese Tiers gelten global für alle Produkte im Shop.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={active}
            onChange={(e) => setActive(e.target.checked)}
            data-testid="product-tiers-active"
          />
          <span>Mengen-Staffelung aktiv</span>
        </label>

        <div className="space-y-2">
          {tiers.map((tier, idx) => (
            <div
              key={idx}
              className="grid grid-cols-1 md:grid-cols-[100px_100px_120px_1fr_60px] gap-2 items-end p-2 rounded-md border bg-gray-50"
              data-testid={`product-tier-row-${idx}`}
            >
              <div>
                <label className="text-xs text-gray-600">Min. Stück</label>
                <Input
                  type="number"
                  min="1"
                  value={tier.min_qty ?? ''}
                  onChange={(e) => updateTier(idx, 'min_qty', e.target.value)}
                />
              </div>
              <div>
                <label className="text-xs text-gray-600">Max. Stück</label>
                <Input
                  type="number"
                  placeholder="∞"
                  value={tier.max_qty ?? ''}
                  onChange={(e) => updateTier(idx, 'max_qty', e.target.value === '' ? null : e.target.value)}
                />
              </div>
              <div>
                <label className="text-xs text-gray-600">Rabatt %</label>
                <Input
                  type="number"
                  step="0.5"
                  value={tier.discount_percent ?? 0}
                  onChange={(e) => updateTier(idx, 'discount_percent', e.target.value)}
                />
              </div>
              <div>
                <label className="text-xs text-gray-600">Label</label>
                <Input
                  type="text"
                  placeholder="z. B. 5er-Pack"
                  value={tier.label ?? ''}
                  onChange={(e) => updateTier(idx, 'label', e.target.value)}
                />
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => removeTier(idx)}
                className="text-red-500 hover:text-red-700"
                data-testid={`product-tier-remove-${idx}`}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          ))}
        </div>

        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={addTier} data-testid="product-tier-add-btn">
            <Plus className="w-4 h-4 mr-1" /> Stufe hinzufügen
          </Button>
          <Badge variant="secondary">{tiers.length} Stufen</Badge>
        </div>

        <Button onClick={save} disabled={saving} data-testid="product-tiers-save-btn">
          {saving ? 'Speichern…' : 'Produkt-Staffelung speichern'}
        </Button>
      </CardContent>
    </Card>
  );
};

export default ProductTiersEditor;
